import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { toast } from 'react-hot-toast';
import axios from 'axios';
import {
  CalculatorIcon,
  CurrencyRupeeIcon,
  ClockIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';

import { formatCurrency, debounce } from '../../utils/helpers';

const EMICalculatorSection = () => {
  const { t, i18n } = useTranslation();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const [formData, setFormData] = useState({
    loanAmount: 5000000,
    interestRate: 8.5,
    tenureYears: 20,
  });

  const [result, setResult] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [settings, setSettings] = useState(null);
  const [cards, setCards] = useState([]);

  // 🔹 Local EMI Calculation Function (Replaces API)
  const localCalculateEMI = (loanAmount, interestRate, tenureMonths) => {
    const r = interestRate / (12 * 100);
    const n = tenureMonths;
    const emi = loanAmount * r * Math.pow(1 + r, n) / (Math.pow(1 + r, n) - 1);
    const totalAmount = emi * n;
    const totalInterest = totalAmount - loanAmount;

    return {
      monthly_emi: emi,
      total_amount: totalAmount,
      total_interest: totalInterest,
    };
  };

  // 🔹 Debounced Calculation
  const calculateEMI = useCallback(
    debounce(async (amount, rate, years) => {
      if (amount < 100000 || rate < 5 || years < 1) return;

      setIsCalculating(true);
      try {
        const data = localCalculateEMI(amount, rate, years * 12);
        setResult(data);
      } catch (error) {
        console.error('EMI calculation failed:', error);
        toast.error('Failed to calculate EMI. Please try again.');
      } finally {
        setIsCalculating(false);
      }
    }, 500),
    []
  );

  useEffect(() => {
    calculateEMI(formData.loanAmount, formData.interestRate, formData.tenureYears);
  }, [formData, calculateEMI]);

  // Fetch settings and cards
  useEffect(() => {
    const fetchSettingsData = async () => {
      try {
        const response = await axios.get('/api/admin/emi-calculator/settings');
        if (response.data.success && response.data.data) {
          setSettings(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching EMI settings:', error);
      }
    };

    const fetchCardsData = async () => {
      try {
        const response = await axios.get('/api/admin/emi-calculator/cards/active');
        if (response.data.success && response.data.data && response.data.data.length > 0) {
          setCards(response.data.data);
        } else {
          // Default fallback cards if DB is empty or fails
          setCards([
            { id: 'default1', card_key: 'principal', title: t('emiCalculator.principal', 'Principal'), icon_name: 'CurrencyRupeeIcon', color_class: 'blue' },
            { id: 'default2', card_key: 'total_interest', title: t('emiCalculator.totalInterest', 'Total Interest'), icon_name: 'ClockIcon', color_class: 'orange' },
            { id: 'default3', card_key: 'total_amount', title: t('emiCalculator.totalAmount', 'Total Amount Payable'), icon_name: 'ChartBarIcon', color_class: 'green' }
          ]);
        }
      } catch (error) {
        console.error('Error fetching EMI cards:', error);
        setCards([
          { id: 'default1', card_key: 'principal', title: t('emiCalculator.principal', 'Principal'), icon_name: 'CurrencyRupeeIcon', color_class: 'blue' },
          { id: 'default2', card_key: 'total_interest', title: t('emiCalculator.totalInterest', 'Total Interest'), icon_name: 'ClockIcon', color_class: 'orange' },
          { id: 'default3', card_key: 'total_amount', title: t('emiCalculator.totalAmount', 'Total Amount Payable'), icon_name: 'ChartBarIcon', color_class: 'green' }
        ]);
      }
    };

    fetchSettingsData();
    fetchCardsData();
  }, [t]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 15,
      },
    },
  };

  // Determine if Tamil is selected
  const isTamil = i18n.language === 'ta';

  return (
    <section id="emi-calculator" className="py-20 bg-gradient-light relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `radial-gradient(circle at 20% 80%, #283079 0%, transparent 50%),
                           radial-gradient(circle at 80% 20%, #283079 0%, transparent 50%),
                           radial-gradient(circle at 40% 40%, #4f46e5 0%, transparent 50%)`,
          }}
        />
      </div>

      <div className="container mx-auto px-4 relative">
        <motion.div
          ref={ref}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          variants={containerVariants}
          className="text-center mb-12"
        >
          <motion.div variants={itemVariants} className="flex justify-center mb-6">
            <div className="bg-primary-100 p-4 rounded-2xl">
              <CalculatorIcon className="h-12 w-12 text-primary-600" />
            </div>
          </motion.div>

          <motion.h2
            variants={itemVariants}
            className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6"
          >
            {isTamil ? t('emiCalculator.title') : (settings?.section_title || t('emiCalculator.title'))}
          </motion.h2>

          <motion.p
            variants={itemVariants}
            className="text-xl text-gray-600 max-w-2xl mx-auto"
          >
            {isTamil ? t('emiCalculator.subtitle') : (settings?.section_subtitle || t('emiCalculator.subtitle'))}
          </motion.p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-7xl mx-auto"
        >
          {/* Calculator Form */}
          <motion.div variants={itemVariants} className="bg-white rounded-3xl shadow-large p-8 lg:p-12">
            <div className="space-y-8">
              {/* Loan Amount */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  {t('emiCalculator.loanAmount')}
                </label>
                <div className="space-y-4">
                  <input
                    type="range"
                    min="100000"
                    max="50000000"
                    step="100000"
                    value={formData.loanAmount}
                    onChange={(e) => handleInputChange('loanAmount', parseInt(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                  />
                  <div className="flex justify-between text-sm text-gray-500">
                    {isTamil ? (
                      <>
                        <span>₹1 லட்சம்</span>
                        <span className="font-semibold text-primary-600 text-lg">
                          {formatCurrency(formData.loanAmount)}
                        </span>
                        <span>₹5 கோடி</span>
                      </>
                    ) : (
                      <>
                        <span>₹1L</span>
                        <span className="font-semibold text-primary-600 text-lg">
                          {formatCurrency(formData.loanAmount)}
                        </span>
                        <span>₹5Cr</span>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Interest Rate */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  {t('emiCalculator.interestRate')}
                </label>
                <div className="space-y-4">
                  <input
                    type="range"
                    min="5"
                    max="20"
                    step="0.1"
                    value={formData.interestRate}
                    onChange={(e) => handleInputChange('interestRate', parseFloat(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                  />
                  <div className="flex justify-between text-sm text-gray-500">
                    <span>5%</span>
                    <span className="font-semibold text-primary-600 text-lg">
                      {formData.interestRate}% {isTamil ? 'வருடத்திற்கு' : 'p.a.'}
                    </span>
                    <span>20%</span>
                  </div>
                </div>
              </div>

              {/* Tenure */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  {t('emiCalculator.tenure')}
                </label>
                <div className="space-y-4">
                  <input
                    type="range"
                    min="1"
                    max="30"
                    step="1"
                    value={formData.tenureYears}
                    onChange={(e) => handleInputChange('tenureYears', parseInt(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                  />
                  <div className="flex justify-between text-sm text-gray-500">
                    <span>1 {isTamil ? 'ஆண்டு' : 'Year'}</span>
                    <span className="font-semibold text-primary-600 text-lg">
                      {formData.tenureYears} {isTamil ? 'ஆண்டுகள்' : 'Years'}
                    </span>
                    <span>30 {isTamil ? 'ஆண்டுகள்' : 'Years'}</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Results */}
          <motion.div variants={itemVariants} className="space-y-8">
            {result && !isCalculating ? (
              <>
                {/* EMI Card */}
                <div className="flex flex-col xl:flex-row gap-6">
                  {/* EMI Card */}
                  <div className="flex-1 bg-white rounded-3xl shadow-soft border border-gray-100 p-8 relative overflow-hidden flex flex-col items-center justify-center min-h-[320px] text-center">
                    <div className="relative z-10 flex flex-col items-center">
                      <div className="flex flex-col items-center mb-4">
                        <CurrencyRupeeIcon className="h-10 w-10 text-primary-600 mb-2" />
                        <span className="text-gray-600 text-lg uppercase tracking-wider font-semibold">
                          {t('emiCalculator.monthlyEmi')}
                        </span>
                      </div>
                      <div className="text-4xl lg:text-5xl font-extrabold mb-2 text-gray-900">
                        {formatCurrency(result.monthly_emi)}
                      </div>
                      <div className="text-gray-500 text-sm font-medium">
                        {isTamil ? t('emiCalculator.monthlyPaymentDetail') : 'Monthly Payment Detail'}
                      </div>
                    </div>
                  </div>

                  {/* Chart Card */}
                  <div className="flex-1 bg-white rounded-3xl shadow-soft p-6 flex flex-col items-center justify-center min-h-[320px]">
                    <h3 className="text-sm font-bold text-gray-500 mb-4 text-center uppercase tracking-wider">
                      {isTamil ? t('emiCalculator.breakupTitle') : 'Break-up of Total Payment'}
                    </h3>
                    <div className="h-64 w-full">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={[
                              { name: isTamil ? t('emiCalculator.principalLabel') : 'Principal Loan Amount', value: formData.loanAmount },
                              { name: isTamil ? t('emiCalculator.interestLabel') : 'Total Interest', value: result.total_interest }
                            ]}
                            cx="50%"
                            cy="50%"
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="value"
                            label={({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
                              const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
                              const x = cx + radius * Math.cos(-midAngle * Math.PI / 180);
                              const y = cy + radius * Math.sin(-midAngle * Math.PI / 180);
                              return (
                                <text x={x} y={y} fill="white" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" className="text-xs font-bold">
                                  {`${(percent * 100).toFixed(0)}%`}
                                </text>
                              );
                            }}
                            labelLine={false}
                          >
                            <Cell fill="#283079" /> {/* Principal - Primary Blue */}
                            <Cell fill="#ed2636" /> {/* Interest - CTA Red */}
                          </Pie>
                          <Tooltip formatter={(value) => formatCurrency(value)} />
                          <Legend verticalAlign="bottom" height={36} iconType="square" />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>

                {/* Other Details */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {cards.map((card, index) => {
                    const IconComponent = {
                      CurrencyRupeeIcon,
                      ClockIcon,
                      ChartBarIcon
                    }[card.icon_name] || CurrencyRupeeIcon;

                    const colorMap = {
                      blue: { bg: 'bg-blue-100', text: 'text-blue-600' },
                      orange: { bg: 'bg-orange-100', text: 'text-orange-600' },
                      green: { bg: 'bg-green-100', text: 'text-green-600' },
                      purple: { bg: 'bg-purple-100', text: 'text-purple-600' },
                      red: { bg: 'bg-red-100', text: 'text-red-600' },
                      teal: { bg: 'bg-teal-100', text: 'text-teal-600' }
                    };

                    const colors = colorMap[card.color_class] || colorMap.blue;

                    // Map card_key to result values
                    const valueMap = {
                      principal: formData.loanAmount,
                      total_interest: result.total_interest,
                      total_amount: result.total_amount
                    };

                    // Localized titles for Tamil
                    const localizedTitleMap = {
                      principal: t('emiCalculator.principalLabel'),
                      total_interest: t('emiCalculator.interestLabel'),
                      total_amount: t('emiCalculator.totalAmount')
                    };

                    const displayTitle = isTamil ? localizedTitleMap[card.card_key] : card.title;

                    return (
                      <div key={card.id} className="bg-white rounded-2xl shadow-soft p-6 flex flex-col items-center text-center">
                        <div className="flex flex-col items-center mb-3">
                          <div className={`${colors.bg} p-3 rounded-xl mb-2`}>
                            <IconComponent className={`h-6 w-6 ${colors.text}`} />
                          </div>
                          <span className="text-gray-600 font-semibold text-sm uppercase tracking-wider">
                            {displayTitle}
                          </span>
                        </div>
                        <div className="text-2xl font-extrabold text-gray-900">
                          {formatCurrency(valueMap[card.card_key] || 0)}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Action Button */}
                <div className="flex justify-center mt-6">
                  <button
                    onClick={() => window.location.href = settings?.cta_link || '/customer-360-eligibility-calculator'}
                    className="bg-[#ed2636] text-white px-6 py-2.5 rounded-xl font-bold text-sm md:text-base transition-all duration-300 shadow-2xl flex items-center justify-center gap-3 w-[60%] sm:w-auto mx-auto hover:bg-red-700"
                  >
                    <span>{isTamil ? t('common.applyNow') : (settings?.cta_text || t('common.applyNow'))}</span>
                  </button>
                </div>
              </>
            ) : (
              <div className="bg-white rounded-3xl shadow-large p-12 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
                <p className="text-gray-600">{isTamil ? 'கணக்கிடுகிறது...' : 'Calculating EMI...'}</p>
              </div>
            )}
          </motion.div>
        </motion.div>
      </div>

      <style jsx>{`
        .slider::-webkit-slider-thumb {
          appearance: none;
          height: 24px;
          width: 24px;
          border-radius: 50%;
          background: #283079;
          cursor: pointer;
          box-shadow: 0 4px 12px rgba(40, 48, 121, 0.4);
          border: 3px solid white;
        }

        .slider::-moz-range-thumb {
          height: 24px;
          width: 24px;
          border-radius: 50%;
          background: #283079;
          cursor: pointer;
          border: 3px solid white;
          box-shadow: 0 4px 12px rgba(40, 48, 121, 0.4);
        }
      `}</style>
    </section>
  );
};

export default EMICalculatorSection;
